import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import * as Yup from 'yup';
import {
  useUpdatePassMutation,
  useCreatePassMutation,
  useLazyListPassesQuery,
} from '../../../../store/redux/api/passApi';
import type { PassState } from '../../../../store/redux/slices/pass.slice';
import type { PassTemplateMeta } from '../../../../store/types/pass';
import {
  passValidationSchema,
  validatePassFields,
} from '../validation/passValidationSchema';
import { buildPassPayload, getAllTemplateFields } from '../utils/buildPassPayload';
import { buildPassEnhancedDataPayload } from '../utils/buildPassEnhancedDataPayload';
import { getStoredFeaturedActions } from '../utils/buildPassEnhancedPreview';
import { mapFeaturedActionsFromApi } from '../../pass-templates-new/utils/mapApiResponseToTemplateState';
import { getEnhancedDesignType } from '../utils/getEnhancedDesignType';
import { hasLocaleContent } from '../utils/passEditState';
import CustomToast from '../../../../common/CustomToast';
import { useApiErrorMessages } from '../../../../hooks/useApiErrorMessages';
import { MeContext } from '../../../../context/MeContext';
import type { MeContextState } from '../../../../store/types/account';

interface UsePassSaveParams {
  isEdit?: boolean;
  isCreate?: boolean;
  uuid?: string;
  passState: PassState;
  template: PassTemplateMeta | undefined;
  templateDetail: any;
  templateId: string;
  pass: any;
  localisationEnabled: boolean;
  setValidationErrors: (errors: Record<string, string>) => void;
}

export const usePassSave = ({
  isEdit,
  isCreate,
  uuid,
  passState,
  template,
  templateDetail,
  templateId,
  pass,
  localisationEnabled,
  setValidationErrors,
}: UsePassSaveParams) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [updatePass, { isLoading: isUpdating }] = useUpdatePassMutation();
  const [createPass, { isLoading: isCreating }] = useCreatePassMutation();
  const [checkExtId] = useLazyListPassesQuery();
  const isSaving = isUpdating || isCreating;
  const { getErrorMessages } = useApiErrorMessages();
  const { state: meState } = useContext(MeContext) as { state: MeContextState };
  const isEnhancedGeneric =
    !!meState.appleEnhancedDesignEnabled &&
    getEnhancedDesignType(templateDetail, templateId) === 'generic';

  const showToast = (
    status: 'success' | 'error',
    title: string,
    description?: string | string[]
  ) => {
    toast({
      render: (props) => (
        <CustomToast
          status={status}
          title={title}
          description={description ?? (status === 'error' ? 'Please try again' : '')}
          onClose={props.onClose}
          position="top"
          duration={2000}
          id={props.id}
        />
      ),
    });
  };

  const handleSave = async () => {
    let schemaErrors: Record<string, string> = {};

    try {
      const schema = isEdit
        ? passValidationSchema.omit(['templateUuid'])
        : passValidationSchema;
      await schema.validate(passState, {
        abortEarly: false,
        context: { isEnhancedGeneric },
      });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        (err.inner || []).forEach((e) => {
          if (e.path) schemaErrors[e.path] = e.message;
        });
      }
    }

    // Validate front and back fields
    if (template) {
      const isStampCard = template.template_type === 'stampCard';
      const activeFields = [
        ...(!isStampCard ? [template.header?.header_one] : []),
        template.header?.header_two,
        template.header?.header_three,
        template.primary?.primary_one,
        template.primary?.primary_two,
        template.secondary?.sec_one,
        template.secondary?.sec_two,
        template.secondary?.sec_three,
        template.auxiliary?.aux_one,
        template.auxiliary?.aux_two,
        template.auxiliary?.aux_three,
        template.back_fields?.back_one,
        template.back_fields?.back_two,
        template.back_fields?.back_three,
        template.back_fields?.back_four,
        template.back_fields?.back_five,
      ].filter((f) => f?.id);

      // Merge pass content defaults with user-edited values
      const passContent = pass?.attributes?.passContent as
        | Record<string, unknown>
        | undefined;
      const effectiveFieldValues = { ...passState.fieldValues };
      if (passContent) {
        activeFields.forEach((f) => {
          if (f?.id && effectiveFieldValues[f.id] === undefined) {
            const key = Object.keys(passContent).find(
              (k) =>
                k.toLowerCase().replace(/\s/g, '') ===
                f.id.toLowerCase().replace(/\s/g, '')
            );
            if (key) {
              const raw = passContent[key];
              const val =
                raw && typeof raw === 'object' && 'value' in raw
                  ? (raw as { value?: string }).value || ''
                  : typeof raw === 'string'
                    ? raw
                    : '';
              if (val) effectiveFieldValues[f.id] = val;
            }
          }
        });
      }

      // Merge template default values
      activeFields.forEach((f) => {
        if (f?.id && effectiveFieldValues[f.id] === undefined && f.default_value) {
          effectiveFieldValues[f.id] = f.default_value;
        }
      });

      const fieldErrors = validatePassFields(
        effectiveFieldValues,
        activeFields as { id: string; label: string }[]
      );
      schemaErrors = { ...schemaErrors, ...fieldErrors };

      // Validate localisation fields
      if (localisationEnabled) {
        const templateAttrsForLangs = templateDetail?.data?.attributes as
          | Record<string, unknown>
          | undefined;
        const templateFieldsForLangs = templateAttrsForLangs?.fields as
          | Record<string, Record<string, { localisation?: Record<string, unknown> } | null>>
          | undefined;
        if (templateFieldsForLangs) {
          // Use one template-wide locale set so partially translated fields follow the same requirements
          const allTemplateLangs = new Set<string>();
          Object.values(templateFieldsForLangs).forEach((section) => {
            if (!section) return;
            Object.values(section).forEach((field) => {
              if (field?.localisation) {
                Object.entries(field.localisation).forEach(([locale, loc]) => {
                  if (hasLocaleContent(loc)) allTemplateLangs.add(locale);
                });
              }
            });
          });
          const allTemplateLangsArray = Array.from(allTemplateLangs);

          activeFields.forEach((f) => {
            if (!f?.id) return;
            if (allTemplateLangsArray.length === 0) return;
            const locales = passState.fieldLocalisations[f.id] || {};

            // Check if user has provided any translation for this field
            const hasAnyTranslation = allTemplateLangsArray.some((locale) => locales[locale]?.trim());
            if (!hasAnyTranslation) return; // No translations provided — backend accepts this

            // User provided at least one translation — all locales are required
            allTemplateLangsArray.forEach((locale) => {
              if (locales[locale]?.trim()) return;
              schemaErrors[`localisation.${f.id}.${locale}`] =
                `${f.label || f.id} translation for ${locale.toUpperCase()} is required`;
            });
          });
        }
      }
    }

    // Check ext ID uniqueness
    if (passState.extId?.trim()) {
      try {
        const result = await checkExtId({
          perPage: 1,
          filters: { extId: passState.extId.trim() },
        }).unwrap();
        const existing = result.data?.[0];
        if (existing && existing.id !== uuid) {
          schemaErrors['extId'] = 'External ID must be unique';
        }
      } catch {
        // Allow save to proceed — the server will reject duplicates
      }
    }

    if (Object.keys(schemaErrors).length > 0) {
      setValidationErrors(schemaErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setValidationErrors({});

    const passContentRaw = pass?.attributes?.passContent as
      | Record<string, unknown>
      | undefined;
    const payload = buildPassPayload(
      passState,
      localisationEnabled,
      passContentRaw,
      template ? getAllTemplateFields(template) : undefined,
      buildPassEnhancedDataPayload({
        featuredActions: passState.featuredActions,
        storedFeaturedActions: mapFeaturedActionsFromApi(
          getStoredFeaturedActions(pass)
        ),
        isCreate,
        isEnhancedGeneric,
      })
    );

    try {
      if (isCreate) {
        if (!passState.templateUuid) return;
        const result = await createPass({
          passTemplateUuid: passState.templateUuid,
          extId: passState.extId || undefined,
          data: payload,
        }).unwrap();
        showToast('success', 'Pass created');
        navigate(`/passes/${result.data.id}`);
      } else {
        if (!uuid) return;
        await updatePass({
          uuid,
          data: { extId: passState.extId || undefined, ...payload },
        }).unwrap();
        showToast('success', 'Pass updated');
        navigate(`/passes/${uuid}`);
      }
    } catch (error) {
      const errorMessages = getErrorMessages(error);
      showToast(
        'error',
        isCreate ? 'Failed to create pass' : 'Failed to update pass',
        errorMessages.length === 1
          ? errorMessages[0]
          : errorMessages.length > 1
            ? errorMessages
            : undefined
      );
    }
  };

  return { handleSave, isSaving };
};
